import { FilterChipComponents } from "@components/common/faro-table/faro-table-filter/faro-table-filter-types";
import { FaroTableToolbar } from "@components/common/faro-table/faro-table-toolbar";
import { BulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { Stack, SxProps, Theme } from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridEventListener,
  GridRowClassNameParams,
  GridRowsProp,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  selectedRowIdsSelector,
  updatingItemsSelector,
} from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
// This is requested by the MUI team: https://mui.com/x/react-data-grid/getting-started/#typescript
import DownArrow from "@assets/icons/new/arrow-down_10px.svg?react";
import UpArrow from "@assets/icons/new/arrow-up_10px.svg?react";
import { FaroCustomCheckbox } from "@components/common/faro-table/faro-custom-checkbox";
import { FaroCustomRow } from "@components/common/faro-table/faro-custom-row";
import { FaroTableEmptyPage } from "@components/common/faro-table/faro-table-empty-page";
import { FaroTableFilter } from "@components/common/faro-table/faro-table-filter/faro-table-filter";
import {
  FaroTableFooter,
  FaroTableFooterProps,
} from "@components/common/faro-table/faro-table-footer";
import { FaroTableSidePanel } from "@components/common/faro-table/faro-table-side-panel";
import { useTablePagination } from "@hooks/table/use-table-pagination";
import { TableEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { UI_HEIGHTS_VIRTUALIZATION_GRID } from "@styles/common-styles";
import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  PaginationMode,
  UseTablePaginationProps,
} from "@hooks/table/use-table-pagination-types";
import { neutral } from "@flat-ui";

declare module "@mui/x-data-grid-pro" {
  // we need to extend the original interface name to allow custom props in footer in slotProps
  interface FooterPropsOverrides extends FaroTableFooterProps {}
}

interface Props<T> {
  /** The entities that the table represent */
  entities: T[];

  /** A key from entity that is unique like ID */
  uniqueIdKey: keyof T;

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** All the columns and their content to show in table */
  columns: GridColDef[];

  /** Callback fired when a row is clicked. */
  onRowClick?: (id: string) => void;

  /** The height of each row. If not provided, the default value will be used */
  rowHeight?: number;

  /** List of all the bulk actions */
  bulkActionButtons?: BulkButtonComponents;

  /** Sort actions for the list view */
  sortActions?: ReactNode;

  /** List of all the filter chips performed on the table */
  filterChips?: FilterChipComponents;

  /** Whether to hide the checkbox on each row or not */
  shouldHideCheckbox?: boolean;

  /** Whether to hide the header or not */
  shouldHideHeader?: boolean;

  /** The current page of the table */
  currentPage?: number;

  /** The amount of items to show per page */
  pageSize?: number;

  /** Customize css */
  sx?: SxProps<Theme>;

  /** Used to apply a custom CSS class on each row */
  rowClassName?: (params: GridRowClassNameParams) => string;

  /** List of column fields to hide. These columns exist to use some logics of them such as filtering */
  hiddenColumns?: string[];

  /** Callback fired when the next page button is clicked */
  onNextPage?: () => Promise<void>;

  /** Callback fired when the previous page button is clicked */
  onPreviousPage?: () => void;

  /** Callback fired when the page is changed */
  onPageChange?: (newPage: number) => void;

  /** Total number of rows to show in the table (if known) */
  totalCount?: number;

  /** Whether there are more items to load from the server (required when totalCount is not provided in server mode) */
  hasMoreItems?: boolean;

  /** Tooltip to show for the next page button when it is disabled */
  disabledNextPageTooltip?: string;

  /** The component that will render for the side panel  */
  sidePanelContent?: ReactNode;

  /** The size of the side panel  */
  sidePanelContentWidth?: string;

  /** The status of the side panel  */
  isSidePanelOpened?: boolean;

  /** The type of the table, used for analytics and tracking */
  tableType: TableType;

  /** Default sorting model of the table */
  defaultSortModel?: GridSortModel;

  /** Callback fired when the sort model changes */
  onSortModelChange?: (model: GridSortModel) => void;

  /**
   * Pagination mode
   * - 'client': All data is available, slice locally
   * - 'server': Data is fetched on demand, may need to load more. Works both with known and unknown totals.
   */
  paginationMode?: PaginationMode;

  /**
   * Controls the visibility of columns in the DataGrid.
   * Each key represents a column field, and the value is a boolean indicating
   * whether the column is visible (`true`) or hidden (`false`).
   */
  columnVisibilityModel?: GridColumnVisibilityModel;

  /**
   * Callback fired when the column visibility model changes.
   * Use this to update state or persist visibility preferences (e.g., in localStorage).
   */
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;

  /** External trigger/menu for column visibility, right-aligned outside the DataGrid header */
  filterMenuTrigger?: ReactNode;

  /** Callback fired when the column order changes (e.g., via drag-and-drop) */
  onColumnOrderChange?: GridEventListener<"columnOrderChange">;
}

const DEFAULT_ROW_HEIGHT_IN_PX = 60;

const DEFAULT_HEADER_HEIGHT_IN_PX = 56;

/** Defines the default amount of items to show per page */
export const DEFAULT_TABLE_PAGE_SIZE = 30;

/**
 * A general component that receives entities and show them in a table.
 * The type of entities is generic <T>, so it can be any type of arrays of object.
 */
export function FaroTable<T>({
  entities,
  uniqueIdKey,
  isLoading,
  columns,
  onRowClick,
  bulkActionButtons,
  sortActions,
  filterChips,
  rowHeight,
  shouldHideCheckbox = false,
  shouldHideHeader = false,
  currentPage = 0,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
  rowClassName,
  sx,
  hiddenColumns,
  onNextPage,
  onPreviousPage,
  onPageChange,
  totalCount,
  hasMoreItems,
  disabledNextPageTooltip,
  sidePanelContent,
  sidePanelContentWidth,
  isSidePanelOpened,
  tableType,
  defaultSortModel,
  onSortModelChange,
  paginationMode = "client",
  columnVisibilityModel,
  filterMenuTrigger,
  onColumnVisibilityModelChange,
  onColumnOrderChange,
}: Props<T>): JSX.Element {
  // Ref to the table container for measuring position
  const tableRef = useRef<HTMLDivElement>(null);
  const [dynamicHeight, setDynamicHeight] = useState<string | undefined>(
    undefined
  );

  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const updatingItems = useAppSelector(updatingItemsSelector);
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();

  // Internal page state management
  const [pageIndex, setPageIndex] = useState(currentPage);

  // Reset page to 0 when entities length suggests a data reset (search/sort changed)
  // This detects when we're on a page that no longer has data
  useEffect(() => {
    const currentPageStart = pageIndex * pageSize;
    if (pageIndex > 0 && currentPageStart >= entities.length) {
      setPageIndex(0);
    }
  }, [entities.length, pageIndex, pageSize]);

  const handlePageChange = useCallback(
    async (newPage: number, direction: "next" | "previous" | "jump") => {
      // Track analytics event
      trackEvent({
        name: TableEvents.changePage,
        props: {
          page: newPage,
          pageToChange: direction,
          tableType,
        },
      });

      // Call optional callbacks based on direction
      if (direction === "next") {
        await onNextPage?.();
      } else if (direction === "previous") {
        onPreviousPage?.();
      }

      // Call generic page change callback
      onPageChange?.(newPage);
    },
    [onNextPage, onPreviousPage, onPageChange, tableType, trackEvent]
  );

  /** Setup server side pagination props if in server mode. For client mode they should be undefined. */
  let serverSidePaginationProps: UseTablePaginationProps<T>["serverSidePaginationProps"];

  if (paginationMode === "server") {
    serverSidePaginationProps = {
      totalCount: totalCount ?? null,
      hasMoreItems,
      fetchNextPage: onNextPage,
    };
  }

  // Unified pagination logic
  const pagination = useTablePagination({
    mode: paginationMode,
    entities,
    pageIndex,
    setPageIndex,
    pageSize,
    serverSidePaginationProps,
    onPageChange: handlePageChange,
  });

  /** Shows an empty page when the data is loaded but nothing exist to show */
  const shouldShowEmptyPage = useMemo(
    () => entities.length === 0 && !isLoading,
    [entities.length, isLoading]
  );

  /**
   * Each row should have an id and the entity.
   * The content of the entity is defined in column to have more flexibility (e.g. custom rendering)
   */
  const rows: GridRowsProp = entities.map((entity) => ({
    /** The unique ID of the entity */
    id: entity[uniqueIdKey],

    /**
     * Only entity is added to the row, so that we can have access to the whole entity
     * in columns and set custom rendering when needed
     */
    entity: entity,
  }));

  /**
   * Dynamically calculates and sets the table height based on its position in the viewport.
   * The height is computed to fill all available vertical space from the table's top position
   * to the bottom of the viewport, minus a small offset. The height is recalculated on window
   * resize to maintain responsiveness across different screen sizes.
   */
  useLayoutEffect(() => {
    if (!tableRef.current) {
      return undefined;
    }

    function updateHeight(): void {
      const rect = tableRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const bottomOffset =
        UI_HEIGHTS_VIRTUALIZATION_GRID.contentPadding +
        UI_HEIGHTS_VIRTUALIZATION_GRID.border;
      setDynamicHeight(`calc(100vh - (${rect.top}px + ${bottomOffset}px))`);
    };

    // Run after layout, and schedule again on the next frame to catch late DOM updates (e.g., banners)
    const requestAnimationFrameId = requestAnimationFrame(updateHeight);

    const resizeObserver = new ResizeObserver(() => updateHeight());
    resizeObserver.observe(document.body);

    window.addEventListener("resize", updateHeight);

    // Recalculate when data/loading state changes (affects layout above the table)
    updateHeight();

    return () => {
      cancelAnimationFrame(requestAnimationFrameId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [entities.length, isLoading]);

  const mergedSx = {
    height: dynamicHeight,
    "& .MuiDataGrid-columnHeaders": {
      zIndex: 2,
      background: neutral[0],
    },
    ...sx,
  };

  return (
    <>
      {filterChips && <FaroTableFilter filterChips={filterChips} />}

      {shouldShowEmptyPage ? (
        <FaroTableEmptyPage />
      ) : (
        <>
          {(bulkActionButtons || sortActions) && (
            <FaroTableToolbar
              numberOfSelectedRows={updatingItems.length}
              numberOfRows={entities.length}
              isLoading={isLoading}
              bulkActionsButtons={bulkActionButtons}
              sortActions={sortActions}
            />
          )}
          <Stack
            ref={tableRef}
            sx={{
              flexDirection: "row",
              gap: sidePanelContent && isSidePanelOpened ? "20px" : "0",
            }}
          >
            {/**
             * Renders the external column visibility trigger/menu, aligned to the right.
             * This element is positioned outside the DataGrid header to ensure it is never virtualized
             * or hidden by horizontal scrolling. Should be provided via the `filterMenuTrigger` prop
             * for maximum flexibility and consistent UX.
             */}
            {filterMenuTrigger && filterMenuTrigger}

            <DataGridPro
              rows={rows}
              sx={mergedSx}
              columns={columns.map((column) => ({
                ...column,
                // Even though the documentation of MUI makes it appear that if the "sortable" value is not set
                // it will default to "false", that is not the case for this version of the data grid component.
                // It used to be the case for previous versions, but for this version it seems that "true" is the default.
                // That's why we have to explicitly default to "false" if the value is not set for the column.
                // eslint-disable-next-line @typescript-eslint/naming-convention -- coming from external package
                sortable: column.sortable ?? false,
              }))}
              slots={{
                /**
                 * We use a custom row to control the visibility of some elements on hover. Every item in the row
                 * where visibility = hidden and class name ${SHOW_ON_HOVER_CLASS}, it will be shown on hover.
                 * This approach is better than using the recommended hoverId state, because it is faster and
                 * reduces the amounts of table rerenders.
                 */
                row: FaroCustomRow,
                baseCheckbox: FaroCustomCheckbox,
                columnSortedAscendingIcon: DownArrow,
                columnSortedDescendingIcon: UpArrow,
                /**
                 * We use a custom footer to show the pagination information.
                 * We don't use the default MUI DataGrid one to have more control over the design, and to go over the
                 * limitations like in projects page where we don't know the total amount of items.
                 */
                footer: FaroTableFooter,
              }}
              slotProps={{
                footer: {
                  paginationModel: pagination.paginationModel,
                  totalItems: pagination.footerData.rowCount,
                  changePage: pagination.handlePageChange,
                  paginationProps: pagination.footerData,
                  shouldDisableNextPage: pagination.shouldDisableNextPage,
                  disabledNextPageTooltip,
                },
              }}
              initialState={{
                columns: {
                  // Hides the columns that should not be visible by adding false to their field
                  columnVisibilityModel: hiddenColumns?.reduce(
                    (acc, key) => ({ ...acc, [key]: false }),
                    {}
                  ),
                },
                sorting: {
                  sortModel: defaultSortModel,
                },
              }}
              // Hides the filter icon on the right side of the header
              disableColumnFilter
              disableColumnMenu
              disableRowSelectionOnClick
              checkboxSelection={!shouldHideCheckbox}
              pagination={true}
              pageSizeOptions={[pageSize]}
              paginationModel={pagination.paginationModel}
              hideFooter={false}
              loading={isLoading}
              columnHeaderHeight={
                shouldHideHeader ? 0 : DEFAULT_HEADER_HEIGHT_IN_PX
              }
              rowHeight={rowHeight ?? DEFAULT_ROW_HEIGHT_IN_PX}
              onRowSelectionModelChange={(newSelectedRowIds) => {
                // Type of newSelectedRowIds is readonly, so we need to make a deep copy to prevent type assertion
                dispatch(setInitialFetchingItems([...newSelectedRowIds]));
              }}
              onRowClick={(row) => onRowClick?.(String(row.id))}
              rowSelectionModel={selectedRowIds}
              getRowClassName={rowClassName}
              onSortModelChange={onSortModelChange}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={onColumnVisibilityModelChange}
              onColumnOrderChange={onColumnOrderChange}
            />
            <FaroTableSidePanel
              isSidePanelOpened={isSidePanelOpened}
              sidePanelContentWidth={sidePanelContentWidth}
              sidePanelContent={sidePanelContent}
            />
          </Stack>
        </>
      )}
    </>
  );
}
